import { CheckCircle2, MessageSquare, Send } from 'lucide-react'
import { BoxTruckSenderLines, BoxTruckMailingService, htmlToSmsText, smsComposerState } from '@numeo/types-main-web-app'
import { ClientQuoIntegration } from '@numeo/types-core'
import { SMS_MAX_BODY_LENGTH } from '@numeo/types'
import { Button } from '@/components/ds2'
import { TiptapEditor } from '@/components/tiptap/TiptapEditor'
import { formatSenderLine, SenderLineSelect } from './SenderLineSelect'
import { ALL_DAYS, DAY_LABELS, MailingFormState } from '../utils/mailing'

/**
 * The stored line as the form knows it, when the picker's own list does not carry it.
 *
 * From the MAILING's own stored number, which is the only place the form may learn it: the server's
 * check answers whether the line still exists and deliberately nothing more, because a line missing
 * from the list is one this caller is not allowed to be shown.
 */
const lineFromStored = (id: string, phoneNumber: string | null) => (phoneNumber ? { id, phoneNumber, name: null } : null)

/** `Dispatch · +14695550101`, or just the number when Quo has no name for the line. */
const inputClass = 'w-full rounded-xl bg-surface px-3 py-2 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent'

/** The "New / Edit mailing" card: name, EST send time, weekday grid, the delivery service (Telegram or
 *  SMS from the company's Quo line) and the rich-text message. Presentational — the page owns the form
 *  state and the save. */
export const MailingForm = ({
    form,
    saving,
    quo,
    senderLines,
    onPatch,
    onSave,
    onCancel,
}: {
    form: MailingFormState
    saving: boolean
    /** The company's Quo workspace, from the application — null when none is connected. */
    quo: ClientQuoIntegration | null
    /** The lines this operator may send from, or null while they are still being fetched / unavailable. */
    senderLines: BoxTruckSenderLines | null
    /** Merge a partial update into the form state (functional set in the page). */
    onPatch: (patch: Partial<MailingFormState>) => void
    onSave: () => void
    onCancel: () => void
}) => {
    const toggleDay = (day: number) => onPatch({ days: form.days.includes(day) ? form.days.filter((d) => d !== day) : [...form.days, day].sort() })
    const text = htmlToSmsText(form.message)
    const quoReady = !!quo && quo.status === 'connected'
    const smsTooLong = form.service === 'sms' && text.length > SMS_MAX_BODY_LENGTH
    /**
     * What this message costs each driver, and whether it may be sent at all.
     *
     * A character outside GSM-7 — an emoji, or the curly apostrophe a paste from Word brings with it —
     * switches the whole message to UCS-2, where a segment is 67 characters instead of 153. One
     * announcement sent that way cost 16 segments per driver and drained the tenant's Quo credits
     * mid-run. The composer is the only place anyone can see that before it happens, so it refuses
     * rather than warns.
     *
     * `priceHint` is the other half: the acute accents are ALLOWED — a fleet that writes Spanish should
     * be able to spell it — and they cost the same. Left unsaid, the segment warning tells the operator
     * to write less, which is the wrong lever when the price is two letters.
     *
     * ONE CALL, from the owner, because this derivation used to be written out in each of the three
     * composers and had already drifted — this one guarded the warning with `!blocked`, the reply box
     * had no warning at all.
     */
    const sms = form.service === 'sms' ? smsComposerState(text) : null
    const cost = sms?.cost ?? null
    const blocked = sms?.blocked ?? null
    const priceHint = sms?.priceHint ?? null
    const segmentWarning = sms?.segmentWarning ?? false

    /**
     * The number this mailing will ACTUALLY leave on.
     *
     * Not `quo.phoneNumber`, which is the workspace FALLBACK and what this hint used to show: a mailing
     * goes out on its creator's own Quo line when they have one, so for every tenant that routes people
     * to their own numbers the old hint named a number the message never came from.
     *
     * The STORED line is the last fallback for the same reason. A mailing can name a line this caller's
     * options do not contain — an admin set it, or Quo has released it — and the save sends that id
     * straight back unchanged, so reading only `senderLines` showed "Texted from <my line>" for a
     * mailing the drivers are texted from quite another number.
     */
    const stored = form.senderPhoneNumberId
    const checked = senderLines?.checked?.id === stored ? senderLines.checked : null
    // Quo no longer has it, so the run falls back to the creator's line whatever the mailing stored.
    const released = !!stored && checked ? !checked.available : false
    const chosen = stored && !released ? (senderLines?.lines.find((line) => line.id === stored) ?? lineFromStored(stored, form.senderPhoneNumber)) : null

    /**
     * Whether "no line named" means the READER's line. Not always.
     *
     * A mailing with no line goes out on its creator's, and editing does not change that — so telling
     * Bob "Texted from <Bob's line>" on Alice's mailing named a number her drivers never see. This
     * page knows neither her line nor her name (the server answers whether it is the reader's, not
     * who the creator is), and will not pretend to: the label says it is the creator's, no number.
     */
    const mine = !form.id || form.createdByMe
    const sending = chosen ?? (mine ? (senderLines?.defaultLine ?? null) : null)
    const sendingLabel = released
        ? `the creator's line — the number this named is no longer in Quo`
        : sending
          ? formatSenderLine(sending)
          : mine
            ? (quo?.phoneNumber ?? 'your Quo line')
            : "the creator's line"

    const services: { id: BoxTruckMailingService; label: string; hint: string; icon: JSX.Element; disabledHint?: string }[] = [
        { id: 'telegram', label: 'Telegram', hint: "Delivered to each driver's connected group", icon: <Send size={15} /> },
        {
            id: 'sms',
            label: 'SMS (Quo)',
            hint: quoReady
                ? `Texted from ${sendingLabel}`
                : quo?.status === 'auth_failed'
                  ? 'Quo rejected the saved API key — reconnect it in Settings → Integrations'
                  : 'Connect Quo in Settings → Integrations to text drivers',
            icon: <MessageSquare size={15} />,
            disabledHint: quoReady ? undefined : 'Not connected',
        },
    ]

    return (
        <div className="rounded-2xl bg-surface p-4 shadow-surface">
            <h2 className="text-sm font-semibold">{form.id ? 'Edit mailing' : 'New mailing'}</h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <label className="text-xs font-medium text-foreground-muted">Name</label>
                    <input
                        className={`mt-1 ${inputClass}`}
                        value={form.name}
                        onChange={(e) => onPatch({ name: e.target.value })}
                        placeholder="Morning check-in"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-foreground-muted">Send time (EST)</label>
                    <input type="time" className={`mt-1 ${inputClass}`} value={form.timeEST} onChange={(e) => onPatch({ timeEST: e.target.value })} />
                </div>
            </div>
            <div className="mt-3">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-foreground-muted">Days</label>
                    <button
                        type="button"
                        onClick={() => onPatch({ days: form.days.length === 7 ? [] : [...ALL_DAYS] })}
                        className="text-xs font-medium text-accent hover:underline"
                    >
                        {form.days.length === 7 ? 'Clear all' : 'Every day'}
                    </button>
                </div>
                <div className="mt-1.5 grid grid-cols-7 gap-1.5">
                    {DAY_LABELS.map((label, day) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => toggleDay(day)}
                            className={`rounded-lg border py-2 text-xs font-semibold transition-colors ${
                                form.days.includes(day)
                                    ? 'border-accent bg-accent text-accent-foreground'
                                    : 'border-border bg-surface text-foreground-muted hover:border-accent/40 hover:text-foreground'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="mt-3">
                <label className="text-xs font-medium text-foreground-muted">Service</label>
                <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {services.map((s) => {
                        const selected = form.service === s.id
                        const disabled = !!s.disabledHint
                        return (
                            <button
                                key={s.id}
                                type="button"
                                disabled={disabled}
                                onClick={() => !selected && onPatch({ service: s.id })}
                                aria-pressed={selected}
                                className={`flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
                                    selected ? 'border-accent bg-accent-soft/60' : 'border-border bg-surface hover:border-accent/40'
                                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                                <div className="flex min-w-0 items-center gap-2.5">
                                    <span
                                        className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
                                            selected ? 'bg-accent text-accent-foreground' : 'bg-default text-foreground-muted'
                                        }`}
                                    >
                                        {s.icon}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-foreground">{s.label}</div>
                                        <div className="truncate text-xs text-foreground-muted" title={s.hint}>
                                            {s.hint}
                                        </div>
                                    </div>
                                </div>
                                {selected ? (
                                    <CheckCircle2 size={17} className="shrink-0 text-accent" />
                                ) : disabled ? (
                                    <span className="shrink-0 text-[11px] font-medium text-foreground-muted">{s.disabledHint}</span>
                                ) : null}
                            </button>
                        )
                    })}
                </div>
            </div>
            {form.service === 'sms' && quoReady && (
                <div className="mt-3">
                    <SenderLineSelect
                        lines={senderLines}
                        value={form.senderPhoneNumberId}
                        stored={form.senderPhoneNumberId ? { id: form.senderPhoneNumberId, phoneNumber: form.senderPhoneNumber } : null}
                        someoneElses={!mine}
                        onChange={(id) => onPatch({ senderPhoneNumberId: id })}
                    />
                </div>
            )}
            <div className="mt-3">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-foreground-muted">Message</label>
                    {form.service === 'sms' ? (
                        // An SMS is the plain text of the composer: formatting is dropped, length and cost are
                        // what the driver's carrier sees. Quo bills per segment, from the company's balance.
                        <span
                            className={`text-[11px] tabular-nums ${smsTooLong || blocked ? 'font-medium text-danger' : segmentWarning ? 'font-medium text-warning' : 'text-foreground-muted/70'}`}
                        >
                            {text.length}/{SMS_MAX_BODY_LENGTH} characters · {cost?.segments ?? 0} segment{cost?.segments === 1 ? '' : 's'} per driver
                        </span>
                    ) : (
                        // Both numbers: the visible-text count AND the enforced raw-HTML budget — markup isn't
                        // free, and showing only the text count made the 'too long' error look unexplainable.
                        <span className="text-[11px] tabular-nums text-foreground-muted/70">
                            {text.length} characters · {form.message.length}/4000 with formatting
                        </span>
                    )}
                </div>
                {/* Same rich-text editor as the broker email templates — formatting survives to Telegram
                    (bold/italic/lists/links via the server's mailingHtmlToTelegramHtml); an SMS keeps the
                    text, bullets and link URLs only. */}
                <div className="mt-1">
                    <TiptapEditor
                        content={form.message}
                        onChange={(html) => onPatch({ message: html })}
                        placeholder="Good morning! Please share your location and availability for today…"
                        minHeight={180}
                    />
                </div>
                {form.service === 'sms' && (
                    <p className="mt-1 text-[11px] text-foreground-muted">Sent as plain text — bold, colours and images are dropped; links are kept as URLs.</p>
                )}
                {/* The refusal names the characters: they are invisible in the editor, and "unsupported
                    characters" sends someone hunting through a thousand of them for an apostrophe that
                    looks exactly like an apostrophe. */}
                {blocked && <p className="mt-1 text-[11px] font-medium text-danger">{blocked}</p>}
                {priceHint && <p className="mt-1 text-[11px] text-foreground-muted">{priceHint}</p>}
                {segmentWarning && (
                    <p className="mt-1 text-[11px] font-medium text-warning">
                        {cost?.segments} segments per driver — {(cost?.segments ?? 0) * Math.max(form.driverAccountIds.length, 1)} in total for this list.
                        Shorter messages cost less and are less likely to run the Quo balance down mid-send.
                    </p>
                )}
            </div>
            <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-foreground-muted">
                    {form.driverAccountIds.length} driver{form.driverAccountIds.length === 1 ? '' : 's'} selected
                </span>
                <div className="flex gap-2">
                    {form.id && (
                        <Button variant="ghost" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                    <Button variant="primary" size="sm" loading={saving} disabled={smsTooLong || !!blocked} onClick={onSave}>
                        {form.id ? 'Save changes' : 'Schedule mailing'}
                    </Button>
                </div>
            </div>
        </div>
    )
}
